/*!
 * @license
 * Alfresco Example Content Application
 *
 * Copyright (C) 2005 - 2020 Alfresco Software Limited
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail.  Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with Alfresco. If not, see <http://www.gnu.org/licenses/>.
 */

import { ProfileState, RuleContext } from '@alfresco/adf-extensions';
import { hasPropertyValue, isAllowedOperation, isInGroup, isType, isTypeViewer } from 'projects/alden-extension/src/lib/rules/alden.rules';
import { GROUP_BOUD_SITE_MANAGER, GROUP_BOUD_SITE_COLLABORATOR, GROUP_BOUD_SITE_CONTRIBUTOR, UDEU_BOUD_TYPE, UDEU_BOUD_DRAFT_STATUS, UDEU_STATUS_PROP, UDEU_BOUD_READY_STATUS, UDEU_BOUD_VALIDATED_STATUS, GROUP_BOUDPUB, GROUP_BOUDED } from '../model/contants/deusto';
import { DELETE_ALLOWABLE_OPERATION, UPDATE_ALLOWABLE_OPERATION } from 'projects/alden-extension/src/lib/models/alden-constants';
import { NodeEntry } from '@alfresco/js-api';

export function isBoudViewer(context: RuleContext): boolean {
  return isTypeViewer(context, 'boud');
}

export function canCreateBoud(context: RuleContext): boolean {
  const profile = context.profile;
  return isInGroup(profile, GROUP_BOUD_SITE_MANAGER) || isInGroup(profile, GROUP_BOUD_SITE_COLLABORATOR);
}

export function canViewBoud(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && isType(nodes[0], UDEU_BOUD_TYPE)) {
    return true;
  }
  return false; 
}

export function canManageBoud(profile: ProfileState): boolean {
  return isInGroup(profile, GROUP_BOUD_SITE_MANAGER) || isInGroup(profile, GROUP_BOUD_SITE_COLLABORATOR) || isInGroup(profile, GROUP_BOUD_SITE_CONTRIBUTOR);
}

export function canEditBoud(context: RuleContext): boolean {
  const profile = context.profile;
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && isInGroup(profile, GROUP_BOUDED) && boudAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION)
    && (hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_BOUD_DRAFT_STATUS) || hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_BOUD_READY_STATUS))) {
    return true;
  }
  return false; 
}

export function canValidateBoud(context: RuleContext): boolean {
  const profile = context.profile;
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count > 0 && isInGroup(profile, GROUP_BOUDED) && nodes.every((node) => boudAllowedOperation(node, UPDATE_ALLOWABLE_OPERATION))
    && nodes.every((node) => hasPropertyValue(node, UDEU_STATUS_PROP, UDEU_BOUD_READY_STATUS))) {
    return true;
  }
  return false; 
}

export function canGeneratePdfBoud(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  const profile = context.profile;
  if(selection.count === 1 && isInGroup(profile, GROUP_BOUDPUB) && boudAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION)
    && (hasPropertyValue(nodes[0], UDEU_STATUS_PROP, UDEU_BOUD_VALIDATED_STATUS))) {
    return true;
  }
  return false; 
}

export function canPublishBoud(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  const profile = context.profile;
  if(selection.count > 0 && isInGroup(profile, GROUP_BOUDPUB) && nodes.every((node) => boudAllowedOperation(node, UPDATE_ALLOWABLE_OPERATION))
    && nodes.every((node) => hasPropertyValue(node, UDEU_STATUS_PROP, UDEU_BOUD_VALIDATED_STATUS))) {
    return true;
  }
  return false; 
}

export function canDeleteBoud(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(context.profile.isAdmin || (selection.count > 0 && nodes.every((node) => boudIsNotPublished(node)) && nodes.every((node) => boudAllowedOperation(node, DELETE_ALLOWABLE_OPERATION)))) {
    return true;
  }
  return false; 
}

function boudAllowedOperation(node: NodeEntry, allowableOperation: string): boolean {
  return isType(node, UDEU_BOUD_TYPE) && isAllowedOperation(node, allowableOperation);
}

function boudIsNotPublished(node: NodeEntry): boolean{
  return hasPropertyValue(node, UDEU_STATUS_PROP, UDEU_BOUD_VALIDATED_STATUS) || hasPropertyValue(node, UDEU_STATUS_PROP, UDEU_BOUD_READY_STATUS) || hasPropertyValue(node,UDEU_STATUS_PROP,UDEU_BOUD_DRAFT_STATUS);
}